/**
 * Stormcaller tier set: Stormscale Regalia (crafting.md §5.4). `bonuses` target the
 * rotation-rework kit (docs/design/classes/stormcaller.md); `interim` uses today's kit.
 * The 2pc's "6% → 9% Lava Surge" is a second 3% proc on the Lava Surge passive (the passive's
 * own chance field is the rework's to name; a second proc reaches the same rate without it).
 */
import { ABILITY, AURA, CLASS } from '@keystone/content';
import type { EffectSpec } from '@keystone/sim';
import { TIER_SET_IDS, tierAdd } from '../../forge.constants';
import { auraRef } from '../../transforms';
import type { TierSetDef } from '../../types';

const S = ABILITY.stormcaller;
const RW = {
  lavaSurgePassive: 'stormcaller.lava_surge_passive',
  lavaSurge: 'stormcaller.lava_surge',
  staticCharge: 'stormcaller.static_charge',
  gatheringStorm: 'stormcaller.gathering_storm',
  earthShock: 'stormcaller.earth_shock',
  tempest: 'stormcaller.tempest',
} as const;

const fullChargeCountsDouble: EffectSpec = {
  type: 'conditional',
  cond: { type: 'selfHasAura', auraId: RW.staticCharge, minStacks: 5 },
  then: [{ type: 'applyAura', to: 'self', aura: auraRef(RW.gatheringStorm) }],
};

export const STORMCALLER_SET: TierSetDef = {
  id: TIER_SET_IDS[CLASS.stormcaller]!,
  name: 'Stormscale Regalia',
  classId: CLASS.stormcaller,
  pieceNames: { helm: 'Stormscale Crown', chest: 'Stormscale Hauberk', legs: 'Stormscale Kilt', gloves: 'Stormscale Gauntlets', boots: 'Stormscale Boots' },
  text: {
    2: { name: 'Molten Coils', description: 'Flame Shock ticks have a 9% chance (was 6%) to grant Lava Surge, and a surged Lava Burst grants 1 Static Charge.' },
    4: { name: 'Eye of the Tempest', description: 'An Earth Shock or Earthquake spent at 5 Static Charge counts double toward Tempest, and Tempest has a 50% chance to grant Lava Surge.' },
  },
  bonuses: {
    2: [
      {
        op: 'addProc',
        auraId: RW.lavaSurgePassive,
        proc: { on: 'damageDealt', abilityIds: [S.flameShock], chance: tierAdd(0.03), icdMs: 4000, effects: [{ type: 'applyAura', to: 'self', aura: auraRef(RW.lavaSurge) }] },
      },
      {
        op: 'addEffects',
        abilityId: S.lavaBurst,
        position: 'start',
        effects: [{ type: 'conditional', cond: { type: 'selfHasAura', auraId: RW.lavaSurge }, then: [{ type: 'applyAura', to: 'self', aura: auraRef(RW.staticCharge) }] }],
      },
    ],
    4: [
      { op: 'addEffects', abilityId: RW.earthShock, position: 'start', effects: [fullChargeCountsDouble] },
      { op: 'addEffects', abilityId: S.earthquake, position: 'start', effects: [fullChargeCountsDouble] },
      { op: 'addEffects', abilityId: RW.tempest, effects: [{ type: 'conditional', cond: { type: 'chance', p: tierAdd(0.5) }, then: [{ type: 'applyAura', to: 'self', aura: auraRef(RW.lavaSurge) }] }] },
    ],
  },
  interim: {
    text: {
      2: { name: 'Molten Coils', description: 'Lightning Bolt has a 15% chance to reset Lava Burst, and Lava Burst generates 6 more Maelstrom.' },
      4: { name: 'Eye of the Tempest', description: 'Earthquake resets Lava Burst and grants 1 Stormkeeper charge.' },
    },
    bonuses: {
      2: [
        {
          op: 'addEffects',
          abilityId: S.lightningBolt,
          effects: [{ type: 'conditional', cond: { type: 'chance', p: tierAdd(0.15) }, then: [{ type: 'modifyCooldown', abilityId: S.lavaBurst, reset: true }] }],
        },
        { op: 'modifyResource', abilityId: S.lavaBurst, field: 'gain', resource: 'maelstrom', amount: 10 + Math.round(tierAdd(6)) },
      ],
      4: [
        {
          op: 'addEffects',
          abilityId: S.earthquake,
          effects: [
            { type: 'modifyCooldown', abilityId: S.lavaBurst, reset: true },
            { type: 'applyAura', to: 'self', stacks: 1, aura: auraRef(AURA.stormkeeper) },
          ],
        },
      ],
    },
  },
};
